import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Supplier {
  id: string
  name: string
  code: string
  country: string
  category: string
  status: string
  payment_terms: string
  rating: number
  total_orders: number
  total_spend: number
  on_time_delivery_rate: number
}

interface PurchaseOrder {
  id: string
  po_number: string
  supplier_name: string
  status: string
  total: number
  currency: string
  expected_delivery: string
}

export default function SupplierManagementPage() {
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [suppliersRes, posRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/suppliers/'),
      fetch(API_BASE + '/suppliers/purchase-orders'),
      fetch(API_BASE + '/suppliers/statistics')
    ])
    if (suppliersRes.ok) setSuppliers((await suppliersRes.json()).suppliers || [])
    if (posRes.ok) setPurchaseOrders((await posRes.json()).purchase_orders || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'received': return 'bg-green-100 text-green-800'
      case 'shipped': return 'bg-blue-100 text-blue-800'
      case 'confirmed': return 'bg-indigo-100 text-indigo-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'submitted': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Supplier Management</h1>
          <div className="space-x-2">
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Add Supplier</button>
            <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">+ Create PO</button>
          </div>
        </div>

        {statistics && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Suppliers</div>
              <div className="text-2xl font-bold">{statistics.total_suppliers}</div>
              <div className="text-xs text-green-600">{statistics.active_suppliers} active</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Purchase Orders</div>
              <div className="text-2xl font-bold">{statistics.total_purchase_orders}</div>
              <div className="text-xs text-yellow-600">{statistics.pending_orders} pending</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Spend YTD</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(statistics.total_spend_ytd)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">On-Time Delivery</div>
              <div className="text-2xl font-bold">{statistics.on_time_delivery_rate}%</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Top Suppliers</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Spend</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {suppliers.slice(0, 8).map(supplier => (
                  <tr key={supplier.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{supplier.name}</div>
                      <div className="text-xs text-gray-500">{supplier.code} • {supplier.country}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm">{supplier.category}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-bold text-green-600">{formatCurrency(supplier.total_spend)}</div>
                      <div className="text-xs text-gray-500">{supplier.total_orders} orders</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <span className="text-yellow-500 mr-1">★</span>
                        <span className="font-medium">{supplier.rating}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Recent Purchase Orders</h3>
            <div className="space-y-3">
              {purchaseOrders.slice(0, 8).map(po => (
                <div key={po.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-mono text-sm">{po.po_number}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(po.status)}`}>{po.status}</span>
                  </div>
                  <div className="text-sm text-gray-600">{po.supplier_name}</div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="font-bold text-green-600">{formatCurrency(po.total, po.currency)}</span>
                    <span className="text-xs text-gray-500">Due: {new Date(po.expected_delivery).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
